import { useCallback, useEffect, useState } from 'react';
import { envoisApi } from '../../api/resources';
import { useSession } from '../../api/session.jsx';
import { Shell } from '../../components/layout/Shell.jsx';
import { Btn } from '../../components/ui/Btn.jsx';
import { Pill } from '../../components/ui/Pill.jsx';
import { C } from '../../theme/palette';
import { BoiteEnvoiCard } from '../reglages/BoiteEnvoiCard.jsx';
import { localeDates, tr, trf } from '../../lib/i18n';

/** Icône et libellé de chaque nature d'envoi. */
const TYPES = {
  RAPPEL_FACTURE: ['🔔', 'Rappel de facture'],
  RAPPEL_RDV: ['📅', 'Rappel de rendez-vous'],
  ALERTE_DEVIS: ['📝', 'Devis qui expire'],
  RELANCE_DEVIS: ['⏰', 'Relance d’une offre sans réponse'],
  FACTURE: ['📄', 'Facture envoyée'],
  DEVIS: ['📋', 'Devis envoyé'],
  PLAN: ['📐', 'Plan envoyé'],
  LETTRE: ['✉️', 'Courrier envoyé'],
  CATALOGUE: ['🛒', 'Catalogue envoyé'],
  FACTURES_COMPTABLE: ['🧮', 'Factures envoyées à la comptable'],
  ATTESTATIONS: ['🏢', 'Attestations de l’entreprise envoyées'],
  RELEVES_COMPTABLE: ['🏦', 'Relevés de banque envoyés à la comptable'],
};

const jourDe = (iso) =>
  new Date(iso).toLocaleDateString(localeDates(), {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });

const heureDe = (iso) =>
  new Date(iso).toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' });

/**
 * Ce qu'une ligne du journal dit d'elle-même.
 *
 * @param {object} e ligne du journal
 * @returns {string} le libellé
 */

/** Pictogramme du canal : e-mail de SwissTech, SMS, ou partage noté par l'écran. */
const CANAL = {
  EMAIL: '📧',
  SMS: '💬',
  LES_DEUX: '📧',
  WHATSAPP: '🟢 WhatsApp ·',
  PARTAGE: '📲 Partagé ·',
};
export function libelleEnvoi(e) {
  const [, nom] = TYPES[e.type] || ['📨', e.type];
  if (e.type === 'RAPPEL_FACTURE') {
    return `Rappel ${e.niveau || 1} · facture ${e.numero || e.reference || ''}`.trim();
  }
  if (e.type === 'FACTURE' || e.type === 'DEVIS') {
    return `${nom} · ${e.numero || e.reference || ''}`.trim();
  }
  return `${nom} · ${e.numero || ''}`.trim();
}

/**
 * Journal des envois faits par le serveur.
 *
 * <p>Un rappel parti sans trace ne se distingue pas d'un rappel jamais parti :
 * chaque tentative est ici, remise ou échec avec son motif. Le patron peut
 * aussi lancer le passage du jour sans attendre l'heure, pour voir ses
 * réglages à l'œuvre.
 *
 * @param {object} props propriétés de la page
 * @param {() => Promise<void>} [props.rechargeFactures] relecture des factures, dont le
 *   passage vient de poser les niveaux de rappel
 * @returns {JSX.Element} la page
 */
/** Envois dont le traitement automatique se souvient : ils restent au journal. */
const AUTOMATIQUES = ['RAPPEL_FACTURE', 'RAPPEL_RDV', 'ALERTE_DEVIS', 'RELANCE_DEVIS'];

export function EnvoisPage({ onNav, soc, estMixte, univers, setUnivers, rechargeFactures }) {
  const { session } = useSession();
  const estPatron = session?.role === 'patron';
  const [page, setPage] = useState(null);
  const [erreur, setErreur] = useState('');
  const [bilan, setBilan] = useState(null);
  const [enCours, setEnCours] = useState(false);
  const [filtre, setFiltre] = useState('tous');
  // La ligne dont on confirme l'effacement.
  const [aEffacer, setAEffacer] = useState(null);

  const charge = useCallback(async () => {
    try {
      setPage(await envoisApi.journal({ page: 0, size: 200 }));
      setErreur('');
    } catch (e) {
      setErreur(e?.message || 'Le journal n’a pas pu être lu.');
    }
  }, []);

  useEffect(() => {
    charge();
  }, [charge]);

  const executer = async () => {
    setEnCours(true);
    setErreur('');
    setBilan(null);
    try {
      const resultat = await envoisApi.executer();
      await charge();
      // Les factures en memoire datent de l'ouverture : le passage vient d'y
      // poser des niveaux de rappel, qu'il faut relire avant de les montrer.
      if (rechargeFactures) await rechargeFactures();
      setBilan(resultat);
    } catch (e) {
      setErreur(e?.message || 'Le traitement n’a pas pu être lancé.');
    } finally {
      setEnCours(false);
    }
  };

  const efface = async (id) => {
    setErreur('');
    try {
      await envoisApi.effaceDuJournal(id);
      setAEffacer(null);
      await charge();
    } catch (e) {
      setErreur(e?.message || 'L’envoi n’a pas pu être effacé.');
    }
  };

  const lignes = (page?.content || []).filter((e) => filtre === 'tous' || e.statut === 'ECHEC');
  const parJour = {};
  lignes.forEach((e) => {
    const j = jourDe(e.envoyeLe);
    (parJour[j] = parJour[j] || []).push(e);
  });
  const jours = Object.keys(parJour);
  const echecs = (page?.content || []).filter((e) => e.statut === 'ECHEC').length;

  return (
    <Shell
      active="plus"
      onNav={onNav}
      soc={soc}
      back
      onBack={() => onNav('plus')}
      estMixte={estMixte}
      univers={univers}
      setUnivers={setUnivers}
      pageTitle="📨 Envois"
    >
      <BoiteEnvoiCard soc={soc} />
      <div
        style={{
          background: C.navyXL,
          border: '1px solid ' + C.border,
          borderRadius: 11,
          padding: '11px 13px',
          marginBottom: 12,
        }}
      >
        <div style={{ fontSize: 11, color: C.ink, fontWeight: 700 }}>
          📨 Ce que le serveur a envoyé
        </div>
        <div style={{ fontSize: 11, color: C.grey, marginTop: 3, lineHeight: 1.5 }}>
          Rappels de facture, rappels de rendez-vous, alertes de devis et pièces envoyées depuis
          l’écran — par e-mail ou SMS, avec le résultat de chaque remise.
        </div>
        <div style={{ marginTop: 9, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <Btn
            label="⚙ Régler les envois automatiques"
            sm
            ghost
            color={C.navy}
            onClick={() => onNav('reglages')}
          />
          {estPatron && (
            <Btn
              label={enCours ? '⏳ Traitement en cours…' : '▶ Lancer le passage du jour'}
              sm
              color={C.teal}
              disabled={enCours}
              onClick={executer}
            />
          )}
        </div>
        {bilan && (
          <div
            data-testid="bilan"
            style={{ marginTop: 8, fontSize: 11, fontWeight: 700, color: C.green }}
          >
            ✓ Passage du {bilan.jour} : {bilan.envoyes} remis · {bilan.echecs} en échec ·{' '}
            {bilan.ignores} sans objet
          </div>
        )}
        {erreur && (
          <div style={{ marginTop: 8, fontSize: 11, fontWeight: 700, color: C.red }}>{erreur}</div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 11 }}>
        {[
          ['tous', 'Tous'],
          ['echecs', 'Échecs' + (echecs ? ' (' + echecs + ')' : '')],
        ].map(([k, l]) => (
          <div
            key={k}
            onClick={() => setFiltre(k)}
            style={{
              padding: '6px 11px',
              borderRadius: 99,
              fontSize: 11,
              fontWeight: 700,
              cursor: 'pointer',
              background: filtre === k ? C.navy : C.white,
              color: filtre === k ? '#fff' : C.grey,
              border: '1px solid ' + (filtre === k ? C.navy : C.border),
            }}
          >
            {l}
          </div>
        ))}
      </div>

      {page && lignes.length === 0 ? (
        <div
          style={{
            textAlign: 'center',
            color: C.muted,
            padding: '44px 0',
            fontSize: 12,
            lineHeight: 1.6,
          }}
        >
          🔕 Aucun envoi {filtre === 'echecs' ? 'en échec' : 'pour l’instant'}.
          <div style={{ fontSize: 11, marginTop: 6 }}>
            Les rappels partent seuls à l’heure réglée, dès que les envois automatiques sont
            activés.
          </div>
        </div>
      ) : (
        jours.map((j) => (
          <div key={j} style={{ marginBottom: 14 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 800,
                color: C.muted,
                textTransform: 'capitalize',
                letterSpacing: 0.3,
                marginBottom: 7,
              }}
            >
              {j}
            </div>
            {parJour[j].map((e) => {
              const [ic] = TYPES[e.type] || ['📨'];
              const echec = e.statut === 'ECHEC';
              const col = echec ? C.red : C.green;
              // Les rappels et alertes automatiques restent : sans eux, ils repartiraient.
              const effacable = estPatron && !AUTOMATIQUES.includes(e.type);
              return (
                <div
                  key={e.id}
                  data-testid="ligne-envoi"
                  style={{
                    background: C.white,
                    border: '1px solid ' + C.border,
                    borderLeft: '3px solid ' + col,
                    borderRadius: 10,
                    padding: '10px 12px',
                    marginBottom: 7,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                    <div
                      style={{
                        width: 34,
                        height: 34,
                        borderRadius: 9,
                        background: col + '16',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 15,
                        flexShrink: 0,
                      }}
                    >
                      {ic}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 11.5, fontWeight: 800, color: C.ink }}>
                        {libelleEnvoi(e)}
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          color: C.muted,
                          marginTop: 2,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {CANAL[e.canal] || '📧'} {e.destinataire}
                        {e.automatique
                          ? ' · automatique'
                          : e.emisPar
                            ? trf(' · envoyé par {0}', e.emisPar)
                            : ' · depuis l’écran'}
                      </div>
                      {echec && e.erreur && (
                        <div style={{ fontSize: 11, color: C.red, marginTop: 3, fontWeight: 600 }}>
                          {e.erreur}
                        </div>
                      )}
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <Pill label={echec ? 'Échec' : 'Envoyé'} color={col} />
                      <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
                        {heureDe(e.envoyeLe)}
                      </div>
                    </div>
                    {effacable && aEffacer !== e.id && (
                      <button
                        type="button"
                        aria-label={tr('Effacer cet envoi')}
                        onClick={() => setAEffacer(e.id)}
                        style={{
                          border: '1px solid ' + C.red + '55',
                          background: C.red + '0d',
                          color: C.red,
                          borderRadius: 9,
                          padding: '8px 10px',
                          fontSize: 14,
                          cursor: 'pointer',
                          flexShrink: 0,
                        }}
                      >
                        🗑
                      </button>
                    )}
                  </div>
                  {aEffacer === e.id && (
                    <div
                      style={{
                        background: C.red + '0d',
                        borderRadius: 9,
                        padding: 10,
                        marginTop: 9,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8,
                      }}
                    >
                      <div style={{ fontSize: 11.5, fontWeight: 700, color: C.ink }}>
                        {tr('Effacer cet envoi du journal ? L’e-mail déjà parti n’est pas repris.')}
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          type="button"
                          onClick={() => efface(e.id)}
                          style={{
                            flex: 1,
                            border: 'none',
                            background: C.red,
                            color: '#fff',
                            borderRadius: 9,
                            padding: 10,
                            fontSize: 12.5,
                            fontWeight: 800,
                            cursor: 'pointer',
                          }}
                        >
                          {tr('Effacer')}
                        </button>
                        <button
                          type="button"
                          onClick={() => setAEffacer(null)}
                          style={{
                            border: '1px solid ' + C.border,
                            background: '#fff',
                            color: C.ink,
                            borderRadius: 9,
                            padding: '10px 14px',
                            fontSize: 12.5,
                            fontWeight: 700,
                            cursor: 'pointer',
                          }}
                        >
                          {tr('Annuler')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))
      )}
    </Shell>
  );
}
